'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import { ICONS, ROUTES } from '@/shared/constants';
import { useAuth } from '@/infrastructure/auth';
import { useRoleSwitch } from '@/infrastructure/auth/useRoleSwitch';
import { useToast } from '@/infrastructure/hooks';
import { useMyProfile } from '@/features/account/hooks/useMyProfile';
import { toBffPictureProxyUrl } from '@/shared/files/picture-proxy';

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

interface UserProfileMenuProps {
  onSignOut?: () => void;
  onRoleSwitching?: (isSwitching: boolean) => void;
  isLoading?: boolean;
}

export function UserProfileMenu({ onSignOut, onRoleSwitching, isLoading = false }: UserProfileMenuProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { roleCode, roles, currentRole, isSwitching, error: switchError, switchTo } = useRoleSwitch();
  const toast = useToast();
  const { data: profile } = useMyProfile();
  const pictureUrl = toBffPictureProxyUrl(profile?.pictureUrl);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // The menu closes before the switch settles, so a refused switch is
  // reported through the app toast instead of inside the menu.
  useEffect(() => {
    if (switchError) toast.error(switchError);
  }, [switchError, toast]);

  const currentRoleLabel = currentRole?.name ?? roleCode ?? 'Role';

  const displayName = user ? `${user.firstName} ${user.lastName}` : '';
  const displayEmail = user?.email ?? '';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRoleChange = async (event: SelectChangeEvent) => {
    const selectedRoleUuid = event.target.value;
    if (roles.length === 0 || isSwitching) return;

    onRoleSwitching?.(true);
    handleClose();
    try {
      await switchTo(selectedRoleUuid);
    } finally {
      onRoleSwitching?.(false);
    }
  };

  const handleSignOut = () => {
    handleClose();
    onSignOut?.();
  };

  return (
    <>
      <Button
        color="inherit"
        aria-label="user profile menu"
        aria-controls={open ? 'user-profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        disabled={!user}
        sx={{
          textTransform: 'none',
          gap: 1,
          px: 1,
        }}
      >
        {user ? (
          <>
            <Avatar
              src={pictureUrl}
              sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
            >
              {getInitials(user.firstName, user.lastName)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'inline-grid' } }}>
              {roles.map((role) => (
                <Typography
                  key={role.uuid}
                  variant="body2"
                  sx={{
                    gridArea: '1/1',
                    visibility: role.code === roleCode ? 'visible' : 'hidden',
                  }}
                >
                  {role.name}
                </Typography>
              ))}
              {roles.length === 0 && (
                <Typography variant="body2" sx={{ gridArea: '1/1' }}>
                  {currentRoleLabel}
                </Typography>
              )}
            </Box>
            <Icon icon={ICONS.EXPAND_MORE} width={20} height={20} />
          </>
        ) : (
          <>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={80} sx={{ display: { xs: 'none', sm: 'block' } }} />
          </>
        )}
      </Button>
      <Menu
        id="user-profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'user-profile-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              mt: 1,
            },
          },
        }}
      >
        {/* User Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={pictureUrl} sx={{ width: 40, height: 40 }}>
            {user ? getInitials(user.firstName, user.lastName) : '?'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {displayEmail}
            </Typography>
          </Box>
        </Box>
        <Divider />

        {/* Role Selector */}
        {roles.length > 0 && [
          <Box key="role-selector" sx={{ px: 2, py: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={currentRole?.uuid ?? ''}
                label="Role"
                onChange={handleRoleChange}
                disabled={isSwitching}
              >
                {roles.map((role) => (
                  <MenuItem key={role.uuid} value={role.uuid}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>,
          <Divider key="role-divider" />,
        ]}

        {/* Menu Items */}
        <MenuItem onClick={() => { handleClose(); router.push(ROUTES.DASHBOARD.SETTINGS.PROFILE.ROOT); }}>
          <ListItemIcon>
            <Icon icon={ICONS.MY_ACCOUNT} width={20} height={20} />
          </ListItemIcon>
          <ListItemText>My Account</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSignOut} disabled={isLoading}>
          <ListItemIcon>
            <Icon icon={ICONS.SIGN_OUT} width={20} height={20} />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
